import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Trophy, Truck, ChevronRight, ExternalLink } from "lucide-react";
import { api, fileUrl, trackingUrl, carrierName } from "@/lib/api";
import { Loader } from "@/components/common";

const TABS = [
  { key: "active", label: "Active", icon: Ticket },
  { key: "wins", label: "Wins", icon: Trophy },
  { key: "past", label: "Past", icon: Ticket },
];

function statusBadge(status) {
  if (status === "drawing") return { text: "DRAWING", cls: "bg-amber-400/15 text-amber-400" };
  if (status === "full") return { text: "FULL", cls: "bg-amber-400/15 text-amber-400" };
  if (status === "drawn") return { text: "ENDED", cls: "bg-slate-500/20 text-slate-300" };
  return { text: "LIVE", cls: "bg-emerald-400/15 text-emerald-400" };
}

function TicketThumb({ photo }) {
  return (
    <div className="w-20 h-20 rounded-xl bg-vault overflow-hidden shrink-0">
      {photo ? <img src={fileUrl(photo)} alt="" className="w-full h-full object-cover" /> : (
        <div className="w-full h-full flex items-center justify-center text-gold/40"><Ticket className="w-7 h-7" /></div>
      )}
    </div>
  );
}

function Row({ t, children, gold }) {
  return (
    <Link to={`/drops/${t.id}`} data-testid={`ticket-${t.id}`}
      className={`card-surface rounded-2xl p-4 flex gap-4 items-center transition-colors hover:border-gold/40 ${gold ? "border-gold/40" : ""}`}>
      <TicketThumb photo={t.prize_photo} />
      <div className="flex-1 min-w-0">{children}</div>
      <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" />
    </Link>
  );
}

export default function Tickets() {
  const [tab, setTab] = useState("active");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/my-tickets").then(({ data }) => setData(data)).catch(() => setData({ active: [], wins: [], past: [], stats: { entered: 0, wins: 0 } })); }, []);

  if (data === null) return <Loader label="Loading your tickets" />;

  const list = data[tab] || [];
  const counts = { active: data.active.length, wins: data.wins.length, past: data.past.length };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="tickets-page">
      <h1 className="font-display text-3xl sm:text-4xl font-black text-white text-center tracking-wide">My Tickets</h1>

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-2 gap-px card-surface rounded-2xl overflow-hidden" data-testid="tickets-stats">
        <div className="flex items-center gap-3 p-5 bg-charcoal/40">
          <span className="w-12 h-12 rounded-xl bg-gold/15 text-gold flex items-center justify-center shrink-0"><Ticket className="w-6 h-6" /></span>
          <div>
            <p className="text-[11px] font-mono text-slate-400 tracking-widest leading-tight uppercase">Drops<br />Entered</p>
            <p className="text-2xl font-black text-white" data-testid="stat-entered">{data.stats.entered}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-5 bg-charcoal/40">
          <span className="w-12 h-12 rounded-xl gold-gradient text-obsidian flex items-center justify-center shrink-0 gold-glow"><Trophy className="w-6 h-6" /></span>
          <div>
            <p className="text-[11px] font-mono text-slate-400 tracking-widest leading-tight uppercase">Wins</p>
            <p className="text-2xl font-black text-white" data-testid="stat-wins">{data.stats.wins}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 grid grid-cols-3 gap-2" data-testid="tickets-tabs">
        {TABS.map((tb) => {
          const on = tab === tb.key;
          return (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
              className={`flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl font-mono text-[11px] sm:text-xs font-bold tracking-widest uppercase border transition-colors ${on ? "gold-gradient text-obsidian border-transparent gold-glow" : "card-surface text-slate-300 border-gold/20 hover:border-gold/50"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
              <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${on ? "bg-obsidian/20" : "bg-gold/15 text-gold"}`}>{counts[tb.key]}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="mt-6 space-y-3" data-testid="tickets-list">
        {list.length === 0 ? (
          <div className="card-surface rounded-2xl p-14 text-center" data-testid="tickets-empty">
            <span className="w-16 h-16 rounded-full bg-gold/10 text-gold flex items-center justify-center mx-auto mb-4">
              {tab === "wins" ? <Trophy className="w-7 h-7" /> : <Ticket className="w-7 h-7" />}
            </span>
            <p className="font-display font-bold text-white text-lg">
              {tab === "active" ? "No active tickets yet" : tab === "wins" ? "No wins yet" : "No past draws yet"}
            </p>
            <p className="text-slate-400 text-sm mt-1">
              {tab === "active" ? "Grab a seat and you're in the draw." : tab === "wins" ? "Your winning draws will show up here." : "Draws you entered will appear here after they're drawn."}
            </p>
            <Link to="/drops" className="inline-flex items-center gap-1.5 mt-4 text-gold font-bold text-sm" data-testid="tickets-browse">
              Browse live drops <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        ) : tab === "active" ? (
          list.map((t) => {
            const b = statusBadge(t.status);
            return (
              <Row key={t.id} t={t}>
                <span className={`inline-block text-[10px] font-mono font-bold px-2 py-0.5 rounded ${b.cls}`}>{b.text}</span>
                <h3 className="font-display font-bold text-white mt-1.5 truncate">{t.title}</h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  {t.my_ticket_count} {t.my_ticket_count === 1 ? "ticket" : "tickets"} · {t.claimed_seats}/{t.total_seats} seats filled
                </p>
              </Row>
            );
          })
        ) : tab === "wins" ? (
          list.map((t) => {
            const shipped = t.order_status === "shipped";
            return (
              <Row key={t.id} t={t} gold>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-gold/15 text-gold"><Trophy className="w-3 h-3" /> YOU WON</span>
                  {t.winning_seats?.length > 0 && <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded border border-gold/40 text-gold">SEAT #{t.winning_seats.join(", #")}</span>}
                  <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${shipped ? "bg-emerald-400/15 text-emerald-400" : "bg-amber-400/15 text-amber-400"}`}>{shipped ? "FULFILLED" : "PENDING"}</span>
                </div>
                <h3 className="font-display font-bold text-white mt-1.5 truncate">{t.title}</h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  {t.my_ticket_count} {t.my_ticket_count === 1 ? "ticket" : "tickets"}{t.drawn_at && ` · Drawn ${new Date(t.drawn_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`}
                </p>
                {shipped && t.tracking_number && (
                  <span onClick={(e) => { e.preventDefault(); window.open(trackingUrl(t.tracking_number), "_blank", "noopener"); }}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-mono text-gold hover:text-gold-bright" data-testid={`ticket-track-${t.id}`}>
                    <Truck className="w-3.5 h-3.5" /> {carrierName(t.tracking_number)} · {t.tracking_number} <ExternalLink className="w-3 h-3" />
                  </span>
                )}
              </Row>
            );
          })
        ) : (
          list.map((t) => (
            <Row key={t.id} t={t}>
              <span className="inline-block text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-slate-500/20 text-slate-300">ENDED</span>
              <h3 className="font-display font-bold text-white mt-1.5 truncate">{t.title}</h3>
              <p className="text-sm text-slate-400 mt-0.5">
                {t.my_ticket_count} {t.my_ticket_count === 1 ? "ticket" : "tickets"}
                {t.winner_names?.length > 0 && ` · Won by @${t.winner_names[0]}${t.winner_names.length > 1 ? ` +${t.winner_names.length - 1}` : ""}`}
              </p>
            </Row>
          ))
        )}
      </div>
    </div>
  );
}
